/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import {
  FlatList,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors';
import { CONTENT_WIDE_BREAKPOINT } from '../../shared/layout/breakpoints';
import { getProductCatalogEventTypeLabel } from '../domain/catalogLabels';
import type { ProductCatalogEventRow } from '../domain/catalogTypes';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { formatLocalDateTime } from '@shared/vueltoDateTime';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { ModalHistoryListFooter } from '../../shared/modals/ModalHistoryListFooter'
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker'
import { ModalStatePanel } from '../../shared/modals/ModalStatePanel';
import { ticketChrome as ticketUi } from '../../shared/ticket/ticketChrome.styles';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';

export type ProductHistoryEventOption = { value: string; label: string };

type Props = {
  visible: boolean;
  productName: string;
  events: ProductCatalogEventRow[];
  historyLoading: boolean;
  historyLoadingMore: boolean;
  historyHasMore: boolean;
  onLoadMore: () => void;
  onClose: () => void;
  actorLabel: (userId: string | null | undefined) => string;
  eventOptions: ProductHistoryEventOption[];
  eventFilter: string;
  onEventFilterChange: (value: string) => void;
  fromDate: string;
  toDate: string;
  onFromDateChange: (value: string) => void;
  onToDateChange: (value: string) => void;
  onApplyFilters: () => void;
  onClearFilters: () => void;
  onExportCsv: () => void;
  eventSummary: (row: ProductCatalogEventRow) => string;
  layoutWide?: boolean;
};

export const ProductHistoryModal = ({
  visible,
  productName,
  events,
  historyLoading,
  historyLoadingMore,
  historyHasMore,
  onLoadMore,
  onClose,
  actorLabel,
  eventOptions,
  eventFilter,
  onEventFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onApplyFilters,
  onClearFilters,
  onExportCsv,
  eventSummary,
  layoutWide = false,
}: Props) => {
  const insets = useSafeAreaInsets();
  const { width: winW, height: winH } = useWindowDimensions();
  const [eventPickerOpen, setEventPickerOpen] = useState(false);

  const modalMaxWidth = layoutWide ? Math.min(860, winW - 48) : Math.min(540, winW - 32);
  const modalMaxHeight = winH * 0.92;
  const isMobile = winW < CONTENT_WIDE_BREAKPOINT && !layoutWide;

  const eventLabel = useMemo(() => {
    return eventOptions.find((o) => o.value === eventFilter)?.label ?? 'Todos';
  }, [eventFilter, eventOptions]);

  const canExportCsv = events.length > 0;

  const hasActiveFilters = eventFilter !== 'all' || fromDate.trim().length > 0 || toDate.trim().length > 0;

  const clearFilters = () => {
    onClearFilters();
  };

  const nestedPicker = (
    <ModalNestedPicker
      visible={eventPickerOpen}
      title="Tipo de evento"
      options={eventOptions}
      selectedValue={eventFilter}
      onSelect={onEventFilterChange}
      onClose={() => setEventPickerOpen(false)}
    />
  );

  const filtersMobile = (
    <View style={styles.histFiltersM}>
      <View style={styles.datesRow}>
        <View style={styles.dateField}>
          <Text style={styles.dateLabel}>Desde</Text>
          <InventoryIsoDateField
            value={fromDate}
            onChange={onFromDateChange}
            emptyLabel="Sin fecha"
            ariaLabel="Fecha desde"
            clearButtonLabel="Quitar"
            iosEmptyBehavior="openPicker"
          />
        </View>
        <View style={styles.dateField}>
          <Text style={styles.dateLabel}>Hasta</Text>
          <InventoryIsoDateField
            value={toDate}
            onChange={onToDateChange}
            emptyLabel="Sin fecha"
            ariaLabel="Fecha hasta"
            clearButtonLabel="Quitar"
            iosEmptyBehavior="openPicker"
          />
        </View>
      </View>

      <TouchableOpacity
        style={styles.pickRow}
        onPress={() => setEventPickerOpen(true)}
        accessibilityRole="button"
        accessibilityLabel="Tipo de evento"
      >
        <View style={styles.pickMeta}>
          <Text style={styles.pickLabel}>Tipo de evento</Text>
          <Text style={[styles.pickValue, eventFilter === 'all' && styles.pickValueMuted]} numberOfLines={1}>
            {eventLabel}
          </Text>
        </View>
        <Text style={styles.pickChev}>▾</Text>
      </TouchableOpacity>

      <View style={styles.filterActions}>
        <TouchableOpacity
          style={[ticketUi.btnGhost, styles.filterActionFlex, !hasActiveFilters && styles.btnQuiet]}
          onPress={clearFilters}
          disabled={!hasActiveFilters}
          accessibilityRole="button"
          accessibilityLabel="Limpiar"
        >
          <Text style={ticketUi.btnGhostText}>Limpiar</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[ticketUi.btnGold, styles.filterActionFlex]}
          onPress={onApplyFilters}
          accessibilityRole="button"
          accessibilityLabel="Aplicar"
        >
          <Text style={ticketUi.btnGoldText}>Aplicar</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const filtersDesktop = (
    <View style={styles.histBar}>
      <View style={styles.histBarRange}>
        <InventoryIsoDateField
          value={fromDate}
          onChange={onFromDateChange}
          emptyLabel="Desde"
          ariaLabel="Fecha desde"
          clearButtonLabel="Quitar"
          iosEmptyBehavior="openPicker"
        />
        <Text style={styles.histBarSep}>→</Text>
        <InventoryIsoDateField
          value={toDate}
          onChange={onToDateChange}
          emptyLabel="Hasta"
          ariaLabel="Fecha hasta"
          clearButtonLabel="Quitar"
          iosEmptyBehavior="openPicker"
        />
      </View>
      <TouchableOpacity
        style={styles.histBarPick}
        onPress={() => setEventPickerOpen(true)}
        accessibilityRole="button"
        accessibilityLabel="Tipo de evento"
      >
        <Text
          style={[styles.histBarPickText, eventFilter === 'all' && styles.pickValueMuted]}
          numberOfLines={1}
        >
          {eventFilter === 'all' ? 'Todos los eventos' : eventLabel}
        </Text>
        <Text style={styles.pickChev}>▾</Text>
      </TouchableOpacity>
      <View style={styles.histBarActions}>
        <TouchableOpacity style={ticketUi.btnGold} onPress={onApplyFilters} accessibilityRole="button">
          <Text style={ticketUi.btnGoldText}>Aplicar</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[ticketUi.btnGhost, !canExportCsv && styles.btnDisabled]}
          onPress={onExportCsv}
          disabled={!canExportCsv}
          accessibilityRole="button"
          accessibilityState={{ disabled: !canExportCsv }}
        >
          <Text style={[ticketUi.btnGhostText, !canExportCsv && styles.btnDisabledText]}>Exportar CSV</Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderRow = ({ item }: { item: ProductCatalogEventRow }) => (
    <View style={styles.listRow}>
      <View style={styles.listRowMain}>
        <Text style={styles.listRowTitle}>{getProductCatalogEventTypeLabel(item.eventType)}</Text>
        <Text style={styles.listRowMeta} numberOfLines={2}>
          {eventSummary(item)}
          {' · '}
          {actorLabel(item.performedByUserId) || '—'}
        </Text>
      </View>
      <Text style={styles.listRowWhen}>{formatLocalDateTime(item.createdAt)}</Text>
    </View>
  );

  const list = historyLoading ? (
    <View style={styles.listRegion}>
      <ModalStatePanel loading loadingMessage="Cargando historial…" />
    </View>
  ) : (
    <FlatList
      data={events}
      keyExtractor={(item) => item.id}
      style={styles.flatList}
      contentContainerStyle={events.length > 0 ? styles.flatListContentGrow : styles.flatListContent}
      ListHeaderComponent={isMobile ? filtersMobile : filtersDesktop}
      onEndReached={() => {
        if (historyHasMore && !historyLoadingMore) onLoadMore();
      }}
      onEndReachedThreshold={0.35}
      nestedScrollEnabled
      keyboardShouldPersistTaps="handled"
      renderItem={renderRow}
      ListEmptyComponent={
        <View style={styles.emptyPinned}>
          <Text style={styles.emptyTitle}>Sin eventos</Text>
          <Text style={styles.emptyMessage}>
            Probá con otras fechas o otro tipo de evento.
          </Text>
        </View>
      }
      ListFooterComponent={
        events.length > 0 ? (
          <ModalHistoryListFooter
            itemCount={events.length}
            hasMore={historyHasMore}
            loadingMore={historyLoadingMore}
            onLoadMore={onLoadMore}
            listNouns={{ singular: 'evento', plural: 'eventos' }}
          />
        ) : null
      }
    />
  );

  if (isMobile) {
    return (
      <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
        <Pressable style={ticketUi.sheetBackdrop} onPress={onClose}>
          <Pressable
            style={[
              ticketUi.sheetCard,
              styles.sheetFlex,
              { paddingBottom: Math.max(insets.bottom, spacing.sm) },
            ]}
            onPress={(e) => e.stopPropagation()}
          >
            <View style={ticketUi.grabber} />
            <TicketModalHeader
              kicker="Catálogo"
              title="Historial del producto"
              subtitle={productName}
              onClose={onClose}
              compact
            />
            <View style={styles.sheetBody}>{list}</View>
            <View style={ticketUi.foot}>
              <TouchableOpacity
                style={[ticketUi.btnGhost, styles.footBtn, !canExportCsv && styles.btnDisabled]}
                onPress={onExportCsv}
                disabled={!canExportCsv}
                accessibilityRole="button"
                accessibilityState={{ disabled: !canExportCsv }}
              >
                <Text style={[ticketUi.btnGhostText, !canExportCsv && styles.btnDisabledText]}>
                  Exportar CSV
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[ticketUi.btnGhost, styles.footBtn]}
                onPress={onClose}
                accessibilityRole="button"
              >
                <Text style={ticketUi.btnGhostText}>Cerrar</Text>
              </TouchableOpacity>
            </View>
            {nestedPicker}
          </Pressable>
        </Pressable>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={[styles.deskBackdrop, { padding: spacing.md }]}>
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <View
          style={[
            ticketUi.dialogCard,
            styles.deskCard,
            { maxHeight: modalMaxHeight, maxWidth: modalMaxWidth, width: '100%' },
          ]}
        >
          <TicketModalHeader
            kicker="Catálogo"
            title="Historial del producto"
            subtitle={productName}
            onClose={onClose}
          />
          <View style={styles.deskBody}>{list}</View>
          <View style={styles.deskFoot}>
            <TouchableOpacity style={ticketUi.btnGhost} onPress={onClose} accessibilityRole="button">
              <Text style={ticketUi.btnGhostText}>Cerrar</Text>
            </TouchableOpacity>
          </View>
        </View>
        {nestedPicker}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  sheetFlex: {
    flex: 1,
    width: '100%',
  },
  sheetBody: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 18,
  },
  footBtn: {
    flex: 1,
  },
  btnDisabled: {
    opacity: 0.4,
  },
  btnDisabledText: {
    color: ticketColors.ink3,
  },
  histFiltersM: {
    gap: 8,
    paddingBottom: 12,
    marginBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
  },
  datesRow: {
    flexDirection: 'row',
    gap: 8,
  },
  dateField: {
    flex: 1,
    gap: 4,
  },
  dateLabel: {
    fontSize: 10,
    fontWeight: '600',
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: ticketColors.ink3,
  },
  pickRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    minHeight: 40,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  pickMeta: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  pickLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  pickValue: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  pickValueMuted: {
    color: ticketColors.ink3,
    fontWeight: '500',
  },
  pickChev: {
    color: ticketColors.ink3,
    fontSize: 12,
  },
  filterActions: {
    flexDirection: 'row',
    gap: 8,
  },
  filterActionFlex: {
    flex: 1,
    minHeight: 36,
  },
  btnQuiet: {
    opacity: 0.45,
  },
  histBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'nowrap',
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.rule,
    marginBottom: 4,
  },
  histBarRange: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  histBarSep: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink3,
  },
  histBarPick: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    height: 36,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: 'transparent',
  },
  histBarPickText: {
    flex: 1,
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  histBarActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    flexShrink: 0,
  },
  listRegion: {
    flex: 1,
    paddingVertical: spacing.md,
  },
  flatList: {
    flex: 1,
  },
  flatListContent: {
    paddingBottom: spacing.sm,
  },
  flatListContentGrow: {
    flexGrow: 1,
    paddingBottom: spacing.md,
  },
  emptyPinned: {
    paddingTop: spacing.sm,
    paddingBottom: spacing.md,
    gap: 4,
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  emptyMessage: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    lineHeight: 16,
  },
  listRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: ticketColors.ruleSoft,
  },
  listRowMain: {
    flex: 1,
    minWidth: 0,
    gap: 4,
  },
  listRowTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
  },
  listRowMeta: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    lineHeight: 16,
  },
  listRowWhen: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink3,
    textAlign: 'right',
    flexShrink: 0,
  },
  deskBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'center',
    alignItems: 'center',
  },
  deskCard: {
    zIndex: 1,
    alignSelf: 'center',
  },
  deskBody: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 16,
  },
  deskFoot: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: ticketColors.rule,
  },
});
